import amazonAds from './amazonAds.js';
import { registerProvider, enableDebug as enableDebug$1, setConsent, hasConsent as hasConsent$1 } from './consent.js';
import googleTags from './google.js';
import { getConfiguredStates, isAffectedState, shouldApplyLDU, getLDUOptions, configureLDUStates } from './limitedDataUse.js';
import metaPixel from './metaPixel.js';
import tddPixel from './tdd.js';
import { parseEventAndPayload } from './utils.js';

// Special key for enabling debug mode via params or localStorage
const DEBUG_KEY = 'bc-debug';
const DEBUG_STORAGE_KEY = 'bc-debug';
const OVERRIDE_CONSENT_KEY = 'bc_tracking_consent';
let debug = false;
const log = (...args) => {
    if (!debug)
        return;
    // eslint-disable-next-line no-console
    console.info(...['[externalTracking]'].concat(args));
};
// Registry of tracking providers
const trackingProviders = {
    Meta: {
        enableDebug: () => metaPixel.enableDebug(),
        track: ({ brandId, pixels, eventName, eventType, eventPayload, trackingOptions }) => metaPixel.track({ brandId, pixels, eventName, eventType, eventPayload, trackingOptions }),
        grantConsent: () => metaPixel.grantConsent(),
        revokeConsent: () => metaPixel.revokeConsent()
    },
    GoogleAds: {
        enableDebug: () => googleTags.enableDebug(),
        track: ({ brandId, pixels, eventName, eventType, eventPayload }) => googleTags.track({ brandId, pixels, eventName, eventType, eventPayload }),
        grantConsent: () => googleTags.grantConsent(),
        revokeConsent: () => googleTags.revokeConsent()
    },
    TTD: {
        enableDebug: () => tddPixel.enableDebug(),
        track: ({ brandId, pixels, eventName, eventType, eventPayload }) => tddPixel.track({ brandId, pixels, eventName, eventType, eventPayload }),
        grantConsent: () => tddPixel.grantConsent(),
        revokeConsent: () => tddPixel.revokeConsent()
    },
    AmazonAds: {
        enableDebug: () => amazonAds.enableDebug(),
        track: ({ brandId, pixels, eventName, eventType, eventPayload }) => amazonAds.track({ brandId, pixels, eventName, eventType, eventPayload }),
        grantConsent: () => amazonAds.grantConsent(),
        revokeConsent: () => amazonAds.revokeConsent()
    }
};
// Register all providers with ConsentManager
Object.values(trackingProviders).forEach(provider => {
    registerProvider(provider);
});
/**
 * Register a new tracking provider
 */
function registerTrackingProvider(providerId, provider) {
    trackingProviders[providerId] = provider;
    registerProvider(provider);
    log(`Registered tracking provider: ${providerId}`);
}
/**
 * Enable debug mode for all providers and consent manager
 */
function enableDebug() {
    if (debug)
        return;
    debug = true;
    // Persist debug mode in localStorage
    try {
        localStorage === null || localStorage === void 0 ? void 0 : localStorage.setItem(DEBUG_STORAGE_KEY, '1');
    }
    catch (e) {
        // Ignore localStorage errors
    }
    log('Debug mode enabled');
    Object.values(trackingProviders).forEach(provider => provider.enableDebug());
    enableDebug$1();
    // Expose debug helpers on window for console testing
    if (typeof window !== 'undefined') {
        window.__bcDebug = {
            // LDU testing helpers
            ldu: {
                getConfiguredStates,
                isAffectedState: (state) => isAffectedState(state),
                shouldApplyLDU: (shipping, billing) => shouldApplyLDU(shipping, billing),
                getLDUOptions: (shipping, billing) => getLDUOptions(shipping, billing),
                configureStates: (states) => configureLDUStates(states),
            },
            // Quick test helper
            testLDU: (shippingState, billingState) => {
                const result = {
                    configuredStates: getConfiguredStates(),
                    shippingState,
                    billingState,
                    shippingAffected: isAffectedState(shippingState),
                    billingAffected: isAffectedState(billingState),
                    shouldApply: shouldApplyLDU(shippingState, billingState),
                    options: getLDUOptions(shippingState, billingState),
                };
                // eslint-disable-next-line no-console
                console.table(result);
                return result;
            },
        };
        log('Debug helpers available at window.__bcDebug');
        log('Try: __bcDebug.testLDU("RI") or __bcDebug.ldu.getConfiguredStates()');
    }
}
/**
 * Disable debug mode for all providers
 */
function disableDebug() {
    debug = false;
    try {
        localStorage === null || localStorage === void 0 ? void 0 : localStorage.removeItem(DEBUG_STORAGE_KEY);
    }
    catch (e) {
        // Ignore localStorage errors
    }
    // eslint-disable-next-line no-console
    console.info('[externalTracking] Debug mode disabled');
}
/**
 * Set user identity for Meta Pixel advanced matching
 */
function setIdentity(identity) {
    metaPixel.setIdentity(identity);
}
/**
 * Grant consent for all tracking providers
 */
function grantConsent() {
    setConsent(true);
}
/**
 * Revoke consent for all tracking providers
 */
function revokeConsent() {
    setConsent(false);
}
/**
 * Check if user has granted tracking consent
 */
function hasConsent() {
    const overrideConsent = localStorage === null || localStorage === void 0 ? void 0 : localStorage.getItem(OVERRIDE_CONSENT_KEY);
    if (overrideConsent) {
        return overrideConsent === 'true';
    }
    return hasConsent$1();
}
/**
 * Extract pixel container from checkoutItems or cartItems if not provided
 * Fallback for single-item cart events
 */
function extractPixelContainerFromCheckoutItems(params, pixelContainer) {
    var _a, _b, _c;
    // If pixelContainer is valid, return as-is
    if ((pixelContainer === null || pixelContainer === void 0 ? void 0 : pixelContainer.brandId) && ((_a = pixelContainer === null || pixelContainer === void 0 ? void 0 : pixelContainer.pixels) === null || _a === void 0 ? void 0 : _a.length)) {
        return pixelContainer;
    }
    // Try to extract from first checkout item or cart item
    const firstItem = ((_b = params === null || params === void 0 ? void 0 : params.checkoutItems) === null || _b === void 0 ? void 0 : _b[0]) || ((_c = params === null || params === void 0 ? void 0 : params.cartItems) === null || _c === void 0 ? void 0 : _c[0]);
    if ((firstItem === null || firstItem === void 0 ? void 0 : firstItem.pixels) && (firstItem === null || firstItem === void 0 ? void 0 : firstItem.brandId)) {
        log('Extracted pixel container from first checkout/cart item');
        return {
            brandId: String(firstItem.brandId),
            pixels: firstItem.pixels,
        };
    }
    return pixelContainer;
}
/**
 * Enrich params with first item's metadata if available
 */
function enrichParamsFromCheckoutItems(params) {
    var _a, _b;
    const firstItem = ((_a = params === null || params === void 0 ? void 0 : params.checkoutItems) === null || _a === void 0 ? void 0 : _a[0]) || ((_b = params === null || params === void 0 ? void 0 : params.cartItems) === null || _b === void 0 ? void 0 : _b[0]);
    if (!firstItem) {
        return params;
    }
    // Only add missing fields from first item
    return Object.assign({ brandId: firstItem.brandId, brandName: firstItem.brandName, categoryId: firstItem.categoryId, categoryName: firstItem.categoryName, subCategoryId: firstItem.subCategoryId, subCategoryName: firstItem.subCategoryName, retailerId: firstItem.retailerId, retailerSku: firstItem.retailerSku, stacklineSku: firstItem.stacklineSku }, params);
}
/**
 * Validate that event has required parameters for tracking
 */
function isValidTrackingEvent(eventName, eventPayload) {
    var _a, _b;
    if (!eventName) {
        return false;
    }
    // Must have at least one identifier: brandId, SKU, or checkoutItems or cartItems
    return !!((eventPayload === null || eventPayload === void 0 ? void 0 : eventPayload.brandId) ||
        (eventPayload === null || eventPayload === void 0 ? void 0 : eventPayload.stacklineSku) ||
        (((_a = eventPayload === null || eventPayload === void 0 ? void 0 : eventPayload.checkoutItems) === null || _a === void 0 ? void 0 : _a.length) > 0) ||
        (((_b = eventPayload === null || eventPayload === void 0 ? void 0 : eventPayload.cartItems) === null || _b === void 0 ? void 0 : _b.length) > 0));
}
/**
 * Check if debug mode should be enabled via params or localStorage
 */
function checkAndEnableDebugMode(params) {
    // Check if debug key is in params (takes priority)
    if (params === null || params === void 0 ? void 0 : params[DEBUG_KEY]) {
        enableDebug();
        // Clean up the debug key from params
        delete params[DEBUG_KEY];
        return;
    }
    // Check localStorage for persisted debug mode
    if (!debug) {
        const debugStored = localStorage === null || localStorage === void 0 ? void 0 : localStorage.getItem(DEBUG_STORAGE_KEY);
        if (debugStored === '1') {
            enableDebug();
        }
    }
}
/**
 * Track an event with all registered providers
 */
function track(event, params, pixelContainer) {
    var _a, _b, _c, _d;
    // Handle debug mode activation
    checkAndEnableDebugMode(params);
    log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    log(`Tracking event: ${event}`, 'Params:', params, 'Pixel Container:', pixelContainer);
    // Extract or enrich pixel container from checkout items if needed
    const enrichedPixelContainer = extractPixelContainerFromCheckoutItems(params, pixelContainer);
    let enrichedParams = params;
    // If single checkout item or cart item, enrich params with item metadata
    if (((_a = params === null || params === void 0 ? void 0 : params.checkoutItems) === null || _a === void 0 ? void 0 : _a.length) === 1 || ((_b = params === null || params === void 0 ? void 0 : params.cartItems) === null || _b === void 0 ? void 0 : _b.length) === 1) {
        enrichedParams = enrichParamsFromCheckoutItems(params);
        log('Enriched params from first checkout/cart item');
    }
    // Validate required parameters
    if (!(enrichedParams === null || enrichedParams === void 0 ? void 0 : enrichedParams.v_id)) {
        log('❌ Missing required visitor ID (v_id)');
        return;
    }
    if (!(enrichedPixelContainer === null || enrichedPixelContainer === void 0 ? void 0 : enrichedPixelContainer.brandId) || !((_c = enrichedPixelContainer === null || enrichedPixelContainer === void 0 ? void 0 : enrichedPixelContainer.pixels) === null || _c === void 0 ? void 0 : _c.length)) {
        log('❌ Missing brand ID or pixels configuration');
        return;
    }
    // Check consent before tracking
    if (!hasConsent$1()) {
        log('❌ Tracking blocked: user consent not granted');
        return;
    }
    // Group pixels by provider
    const pixelsByProvider = enrichedPixelContainer.pixels.reduce((acc, pixel) => {
        acc[pixel.pixelProvider] = [...(acc[pixel.pixelProvider] || []), pixel];
        return acc;
    }, {});
    // Build tracking options for providers
    // Currently supports LDU (Meta Limited Data Use) - extensible for future controls
    const shippingState = enrichedParams === null || enrichedParams === void 0 ? void 0 : enrichedParams.shippingState;
    const billingState = enrichedParams === null || enrichedParams === void 0 ? void 0 : enrichedParams.billingState;
    const lduOptions = getLDUOptions(shippingState, billingState);
    const trackingOptions = lduOptions ? { ldu: lduOptions } : null;
    if ((_d = trackingOptions === null || trackingOptions === void 0 ? void 0 : trackingOptions.ldu) === null || _d === void 0 ? void 0 : _d.enabled) {
        log(`LDU will be applied - shippingState: ${shippingState}, billingState: ${billingState}`);
    }
    // Track with each provider
    Object.entries(pixelsByProvider).forEach(([providerName, providerPixels]) => {
        const provider = trackingProviders[providerName];
        if (!provider) {
            log(`Provider not registered: ${providerName}`);
            return;
        }
        try {
            providerPixels.forEach((pixel) => {
                const { eventName, eventType, eventPayload } = parseEventAndPayload(event, enrichedParams, pixel.eventMapping);
                if (!isValidTrackingEvent(eventName, eventPayload) || !eventName) {
                    log('Event not supported or missing identifiers:', event);
                    return;
                }
                log('✅ Consent granted, event is valid, proceeding with tracking');
                provider.track({
                    brandId: enrichedPixelContainer.brandId,
                    pixels: [pixel],
                    eventName,
                    eventType: eventType,
                    eventPayload,
                    // Pass tracking options to providers (each extracts what it needs)
                    trackingOptions,
                });
                log(`Tracked with ${providerName}:`, eventName);
            });
        }
        catch (error) {
            log(`Error tracking with ${providerName}:`, error);
        }
    });
}

export { disableDebug, enableDebug, grantConsent, hasConsent, registerTrackingProvider, revokeConsent, setIdentity, track };
//# sourceMappingURL=externalTracking.js.map
